import { CoinIcon, Icon, VaultIcon } from "../components/Common.jsx";
import { fmtGBP, fmtNumber, fmtPence } from "../lib/utils.js";
import { previewRateChange } from "../lib/rateChange.js";
import { isYoungChild } from "../lib/age.js";
import { t as ct } from "../lib/content.js";
const { useState, useMemo } = React;

// LB-355 — changing what a token is worth. Design signed off 21 Sep (canvas
// U32CGrk98PhFGKerhQwEb5, boards A1–A4, K1–K3): Option A, three steps.
//
//   1  Tokens they already have   forced choice, per-child figures, Cancel
//   2  Rewards from now on        forced choice, words only, link to the detail view
//   3  Check and confirm          step 1's table, one line each for rewards, the money-value
//                                 reassurance and the kid note; confirm names the new value
//   detail                        one view, opened from 2 or 3: child switcher, balance,
//                                 rewards grouped old → new, then everything that keeps its
//                                 money value
//
// Every figure on every step comes from previewRateChange (lib/rateChange.js), which runs the
// same function the confirm button runs on a copy of the household. The screens cannot show
// one thing and do another.
//
// Copy rules from the sign-off: no fractions (no half, no double) — more/less and
// increase/decrease, because odd rates give odd fractions; an unchanged figure reads
// "no change", never "same". Every string goes through ct().

// What one token is worth, as the steps say it: "5p", "£1".
export function tokenValueLabel(rate) {
  const r = rate > 0 ? rate : 10;
  return r === 1 ? "£1" : `${Math.round(100 / r)}p`;
}

// Colour rule (Alan, 22 Sep): red means a child's money value goes DOWN. Nothing else is
// coloured — tokens, unchanged worth and rising worth are all plain ink — so the one red
// figure on a screen is the one thing worth a second look.
const worthClass = (penceBefore, penceAfter) => (penceAfter < penceBefore ? "text-rose-700" : "text-slate-900");

function Arrow() {
  return <span className="text-slate-400 mx-0.5" aria-hidden="true">→</span>;
}

// The step 1 / step 3 table: child, tokens, worth.
function BalanceTable({ rows }) {
  const noChange = (v) => ct("settings.rate_change.no_change", "{value}, no change", { value: v });
  return (
    <table className="w-full text-sm mt-2" data-testid="rate-balance-table">
      <thead>
        <tr className="text-xs text-slate-500">
          <th className="text-left font-bold pb-1 w-[28%]"><span className="sr-only">Child</span></th>
          <th className="text-left font-bold pb-1">{ct("settings.rate_change.col_tokens", "Tokens")}</th>
          <th className="text-left font-bold pb-1">{ct("settings.rate_change.col_worth", "Worth")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-t border-slate-100 align-top" data-testid={`rate-row-${r.id}`}>
            <td className="py-1.5 pr-2 font-bold text-slate-900">{r.name}</td>
            <td className="py-1.5 pr-2 font-bold text-slate-900 tabular-nums">
              {r.before === r.after ? noChange(fmtNumber(r.before)) : <>{fmtNumber(r.before)}<Arrow />{fmtNumber(r.after)}</>}
            </td>
            <td className={`py-1.5 font-bold tabular-nums ${worthClass(r.penceBefore, r.penceAfter)}`}>
              {r.penceBefore === r.penceAfter ? noChange(fmtPence(r.penceBefore)) : <>{fmtPence(r.penceBefore)}<Arrow />{fmtPence(r.penceAfter)}</>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Option({ id, selected, onSelect, title, body, children }) {
  return (
    <label className={`flex gap-3 p-3.5 bg-white rounded-lg border-2 cursor-pointer ${selected ? "border-slate-900" : "border-slate-200"}`} data-testid={`rate-option-${id}`}>
      <input type="radio" name={`rate-${id.split("-")[0]}`} checked={selected} onChange={onSelect} className="mt-0.5 w-5 h-5 flex-shrink-0 accent-slate-900" />
      <span className="flex-1 min-w-0 block">
        <span className="block text-base font-bold text-slate-900">{title}</span>
        <span className="block text-sm text-slate-600 mt-0.5">{body}</span>
        {children}
      </span>
    </label>
  );
}

function InfoCard({ icon, children, testid }) {
  return (
    <div className="flex gap-3 items-start bg-white rounded-lg p-3 text-sm text-slate-700" data-testid={testid}>
      <span className="flex-shrink-0 text-slate-700 mt-0.5">{icon}</span>
      <span className="flex-1 min-w-0">{children}</span>
    </div>
  );
}

const rowsOf = (pv) => pv.perChild.map((c) => ({ id: c.id, name: c.name, before: c.balance.before, after: c.balance.after, penceBefore: c.balance.penceBefore, penceAfter: c.balance.penceAfter }));

/* The flow. Rendered by TokenSetupFields in Settings once a parent picks a different value.
 * onConfirm({ balances, rewards }) applies; onCancel stores nothing. */
export function RateChangeFlow({ state, toRate, onCancel, onConfirm }) {
  const fromRate = state.settings.exchangeRate > 0 ? state.settings.exchangeRate : 10;
  const [step, setStep] = useState(1);
  const [balances, setBalances] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [detailFrom, setDetailFrom] = useState(null); // 2 | 3 | null
  const cheaper = toRate > fromRate; // more tokens to the pound: each one is worth less
  const dir = cheaper ? "less" : "more";
  const from = tokenValueLabel(fromRate);
  const to = tokenValueLabel(toRate);

  // Both balance answers are needed on step 1, so both are previewed. Rewards are previewed
  // as "rebalance" until chosen: it is the answer that changes something to show.
  const pvMoney = useMemo(() => previewRateChange(state, toRate, { balances: "money", rewards: rewards || "rebalance" }), [state, toRate, rewards]);
  const pvTokens = useMemo(() => previewRateChange(state, toRate, { balances: "tokens", rewards: rewards || "rebalance" }), [state, toRate, rewards]);
  const pv = balances === "tokens" ? pvTokens : pvMoney;

  // The goal a parent would recognise: the first child with one, their top goal.
  const goalChild = pvMoney.perChild.find((c) => c.goals.length);
  const goal = goalChild ? goalChild.goals[0] : null;
  const goalMovedUnderTokens = goalChild ? pvTokens.perChild.find((c) => c.id === goalChild.id).goals[0] : null;

  const title = ct("settings.rate_change.title", "Change 1 token from {from} to {to}", { from, to });
  const stepNames = [
    ct("settings.rate_change.step1_name", "Tokens they already have"),
    ct("settings.rate_change.step2_name", "Rewards from now on"),
    ct("settings.rate_change.step3_name", "Check"),
  ];
  const noted = (balances ? pv : pvMoney).perChild.filter((c) => c.note);
  const notedNames = noted.map((c) => c.name);
  const namesList = notedNames.length <= 1 ? notedNames.join("") : `${notedNames.slice(0, -1).join(", ")} and ${notedNames[notedNames.length - 1]}`;
  const anyTokenNote = noted.some((c) => c.note.kind === "tokens");

  if (detailFrom) {
    return <RateChangeDetail pv={pv} rewards={rewards} title={title} onClose={() => setDetailFrom(null)} />;
  }

  const primary = step === 3
    ? { label: ct("settings.rate_change.confirm", "Change to {to}", { to }), enabled: true, onClick: () => onConfirm({ balances, rewards }) }
    : { label: ct("settings.rate_change.continue", "Continue"), enabled: step === 1 ? !!balances : !!rewards, onClick: () => setStep(step + 1) };
  const secondary = step === 1
    ? { label: ct("settings.rate_change.cancel", "Cancel"), onClick: onCancel }
    : { label: ct("settings.rate_change.back", "Back"), onClick: () => setStep(step - 1) };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 md:flex md:justify-center" data-testid="rate-change">
    {/* The app's own column, not the window (Alan, 22 Sep): full width on a phone, --shell-max on anything wider. */}
    <div className="bg-canvas flex flex-col w-full h-full md:max-w-[var(--shell-max)]" role="dialog" aria-modal="true" aria-label={title}>
      <div className="bg-white border-b border-slate-200 flex items-center gap-2.5 px-4 py-3" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}>
        <button type="button" onClick={secondary.onClick} className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-900 flex-shrink-0" aria-label={secondary.label}>
          <Icon id="ic-back" size={16} style={{ color: "currentColor" }} />
        </button>
        <div className="text-[15px] font-bold text-slate-900 truncate">{title}</div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
        <div className="flex gap-1.5" aria-hidden="true">
          {[1, 2, 3].map((n) => <i key={n} className={`block w-7 h-1 rounded ${n <= step ? "bg-slate-900" : "bg-slate-300"}`} />)}
        </div>
        <div className="text-xs font-bold text-slate-500" data-testid="rate-step">
          {ct("settings.rate_change.step", "Step {n} of 3", { n: step })} · <b className="text-slate-900">{stepNames[step - 1]}</b>
        </div>

        {step === 1 && (
          <>
            <h1 className="text-xl font-extrabold text-slate-900 leading-tight">{ct("settings.rate_change.q1_heading", "What should happen to the tokens your kids already have?")}</h1>
            <p className="text-sm text-slate-600">{ct("settings.rate_change.q1_lead", "Each token will be worth {dir}. You can {adjust} tokens so balances keep their current money value, or leave them as they are.", { dir, adjust: cheaper ? "add" : "take away" })}</p>
            <Option id="balances-money" selected={balances === "money"} onSelect={() => setBalances("money")}
              title={ct("settings.rate_change.money_title", "Keep their money the same")}
              body={ct("settings.rate_change.money_body", "We {adjust} tokens so each balance is worth the same money as today.", { adjust: cheaper ? "add" : "take away" })}>
              <BalanceTable rows={rowsOf(pvMoney)} />
              {goal && <span className="block text-xs text-slate-500 mt-2">{ct("settings.rate_change.money_goals", "Goal progress doesn't change.")}</span>}
            </Option>
            <Option id="balances-tokens" selected={balances === "tokens"} onSelect={() => setBalances("tokens")}
              title={ct("settings.rate_change.tokens_title", "Keep their tokens the same")}
              body={ct("settings.rate_change.tokens_body", "Balances stay as they are, so each one is worth {dir} money than today.", { dir })}>
              <BalanceTable rows={rowsOf(pvTokens)} />
              {goalMovedUnderTokens && goalMovedUnderTokens.pctBefore !== goalMovedUnderTokens.pctAfter && (
                <span className="block text-xs text-slate-500 mt-2">{ct("settings.rate_change.tokens_goals", "Goal progress goes {updown}. {name}'s {goal} goes from {from}% to {to}%.", { updown: goalMovedUnderTokens.pctAfter < goalMovedUnderTokens.pctBefore ? "down" : "up", name: goalChild.name, goal: goalMovedUnderTokens.title, from: goalMovedUnderTokens.pctBefore, to: goalMovedUnderTokens.pctAfter })}</span>
              )}
            </Option>
            <InfoCard icon={<Icon id="ic-goals" size={18} style={{ color: "currentColor" }} />} testid="rate-goal-note">
              {goal
                ? ct("settings.rate_change.goal_note", "Goals keep their price in pounds. {name}'s {price} {goal} will need {to} tokens instead of {from}.", { name: goalChild.name, price: fmtPence(goal.pence), goal: goal.title, to: fmtNumber(goal.after), from: fmtNumber(goal.before) })
                : ct("settings.rate_change.goal_note_plain", "Goals keep their price in pounds, so their token targets follow the new value.")}
            </InfoCard>
            <InfoCard icon={<VaultIcon className="w-[18px] h-[18px]" />} testid="rate-vault-note">
              {ct("settings.rate_change.vault_note", "Savings Vault balances are kept in pounds, so they don't change.")}
            </InfoCard>
          </>
        )}

        {step === 2 && (
          <>
            <h1 className="text-xl font-extrabold text-slate-900 leading-tight">{ct("settings.rate_change.q2_heading", "What should happen to rewards?")}</h1>
            <p className="text-sm text-slate-600">{ct("settings.rate_change.q2_lead", "This only affects tokens earned after the change.")}</p>
            <Option id="rewards-rebalance" selected={rewards === "rebalance"} onSelect={() => setRewards("rebalance")}
              title={ct("settings.rate_change.rebalance_title", "Rebalance rewards to fit each child's cap")}
              body={ct("settings.rate_change.rebalance_body", "We resize every reward so a busy week reaches that child's weekly cap.")} />
            <Option id="rewards-keep" selected={rewards === "keep"} onSelect={() => setRewards("keep")}
              title={ct("settings.rate_change.keep_title", "Keep rewards as they are")}
              body={ct("settings.rate_change.keep_body", "Rewards stay the same in tokens, so each one is worth {dir} money.", { dir })} />
            <button type="button" className="self-start text-sm font-bold text-slate-900 flex items-center gap-1" onClick={() => setDetailFrom(2)} data-testid="rate-see-detail">
              {ct("settings.rate_change.see_detail", "See what changes for each child")}<Icon id="ic-chevron-right" size={14} style={{ color: "currentColor" }} />
            </button>
          </>
        )}

        {step === 3 && (
          <>
            <h1 className="text-xl font-extrabold text-slate-900 leading-tight">{ct("settings.rate_change.q3_heading", "Here's what will change")}</h1>
            <div className="bg-white rounded-lg p-3">
              <div className="text-xs font-bold text-slate-500">{ct("settings.rate_change.balances_label", "Token balances")}</div>
              <BalanceTable rows={rowsOf(pv)} />
            </div>
            <InfoCard icon={<Icon id="ic-check" size={18} style={{ color: "currentColor" }} />} testid="rate-line-rewards">
              {rewards === "rebalance"
                ? ct("settings.rate_change.line_rebalanced", "Rewards rebalanced to fit each child's weekly cap.")
                : ct("settings.rate_change.line_kept", "Rewards stay the same in tokens.")}
            </InfoCard>
            <InfoCard icon={<VaultIcon className="w-[18px] h-[18px]" />} testid="rate-line-money">
              {ct("settings.rate_change.line_money", "Goals, experience rewards, weekly caps and Savings Vault keep their money value, but the token amount changes.")}
            </InfoCard>
            {notedNames.length > 0 && (
              <InfoCard icon={<Icon id="ic-bell" size={18} style={{ color: "currentColor" }} />} testid="rate-line-notes">
                {anyTokenNote
                  ? ct("settings.rate_change.line_notes", "{names} will see a short note explaining why their tokens changed.", { names: namesList })
                  : ct("settings.rate_change.line_notes_goal", "{names} will see a short note explaining why the token value of their goal changed.", { names: namesList })}
              </InfoCard>
            )}
            <button type="button" className="w-full bg-white border border-slate-200 rounded-lg py-3 text-sm font-bold text-slate-900 flex items-center justify-center gap-1" onClick={() => setDetailFrom(3)} data-testid="rate-see-full">
              {ct("settings.rate_change.see_full", "See full detail for each child")}<Icon id="ic-chevron-right" size={14} style={{ color: "currentColor" }} />
            </button>
          </>
        )}
      </div>

      <div className="bg-white border-t border-slate-200 p-4 flex flex-col gap-2" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}>
        <button type="button" disabled={!primary.enabled} onClick={primary.onClick} data-testid="rate-primary"
          className={`w-full rounded-lg py-3 text-base font-bold ${primary.enabled ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"}`}>{primary.label}</button>
        {!primary.enabled && <div className="text-center text-xs text-slate-500">{ct("settings.rate_change.choose_one", "Choose one to continue")}</div>}
        <button type="button" onClick={secondary.onClick} className="w-full py-2 text-sm font-bold text-slate-900" data-testid="rate-secondary">{secondary.label}</button>
      </div>
    </div>
    </div>,
    document.body
  );
}

const tokensToPenceAt = (n, rate) => Math.round((n * 100) / rate);

// Board A4. One view, from step 2 or step 3.
function RateChangeDetail({ pv, rewards, title, onClose }) {
  const [childId, setChildId] = useState(pv.perChild[0]?.id);
  const c = pv.perChild.find((x) => x.id === childId) || pv.perChild[0];
  const fromRate = pv.fromRate, toRate = pv.toRate;
  const learnLabel = {
    spellingReward: ct("settings.rate_change.lbl_spelling_full", "Spelling, full marks"),
    spellingPassReward: ct("settings.rate_change.lbl_spelling_pass", "Spelling pass"),
    vocabFullReward: ct("settings.rate_change.lbl_vocab_full", "Vocabulary, full marks"),
    vocabPassReward: ct("settings.rate_change.lbl_vocab_pass", "Vocabulary pass"),
    ttStreakFullReward: ct("settings.rate_change.lbl_tt_full", "Times tables, full marks"),
    ttStreakPassReward: ct("settings.rate_change.lbl_tt_pass", "Times tables pass"),
    mathsFullReward: ct("settings.rate_change.lbl_maths_full", "Maths workout, full marks"),
    mathsPassReward: ct("settings.rate_change.lbl_maths_pass", "Maths workout pass"),
  };
  const labelOf = (r) => (r.group === "habit" ? ct("settings.rate_change.lbl_habit", "Every task done for the week") : r.group === "learn" ? learnLabel[r.key] : r.label);
  const after = new Map(c.earning.after.map((r) => [r.key, r]));
  const rewardsHeading = rewards === "keep"
    ? ct("settings.rate_change.detail_rewards_kept", "Rewards · kept as they are")
    : c.cap.gbp === null
      ? ct("settings.rate_change.detail_rewards_uncapped", "Rewards · no weekly cap, so they stay as they are")
      : ct("settings.rate_change.detail_rewards_rebalanced", "Rewards · rebalanced to fit {cap} a week", { cap: fmtPence(Math.round(c.cap.gbp * 100)) });
  const groups = [
    ["habit", ct("settings.rate_change.group_habit", "Weekly reward for daily tasks")],
    ["tasks", ct("settings.rate_change.group_tasks", "Earning activities")],
    ["learn", ct("settings.rate_change.group_learn", "Learn & Earn")],
  ];
  const tokensCell = (a, b) => (b === undefined
    ? <span className="text-slate-500">{ct("settings.rate_change.removed", "removed to fit the cap")}</span>
    : a === b ? fmtNumber(a) : <>{fmtNumber(a)}<Arrow />{fmtNumber(b)}</>); // an unchanged figure is shown as itself (Alan, 22 Sep)
  const worthCell = (a, b) => (b === undefined ? "" : tokensToPenceAt(a, fromRate) === tokensToPenceAt(b, toRate) ? fmtGBP(a, fromRate) : <>{fmtGBP(a, fromRate)}<Arrow />{fmtGBP(b, toRate)}</>);
  const spendAfter = new Map(c.spend.after.map((r) => [r.key, r]));

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 md:flex md:justify-center" data-testid="rate-detail">
    <div className="bg-canvas flex flex-col w-full h-full md:max-w-[var(--shell-max)]" role="dialog" aria-modal="true" aria-label={title}>
      <div className="bg-white border-b border-slate-200 flex items-center gap-2.5 px-4 py-3" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}>
        <button type="button" onClick={onClose} className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-900 flex-shrink-0" aria-label={ct("settings.rate_change.back", "Back")}>
          <Icon id="ic-back" size={16} style={{ color: "currentColor" }} />
        </button>
        <div className="text-[15px] font-bold text-slate-900 truncate">{ct("settings.rate_change.detail_title", "What changes for each child")}</div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
        {/* Board A4: a segmented switcher, then three white cards. */}
        {pv.perChild.length > 1 && (
          <div className="flex gap-1 p-1 bg-pill rounded-lg" role="tablist">
            {pv.perChild.map((x) => (
              <button key={x.id} type="button" role="tab" aria-selected={x.id === c.id} onClick={() => setChildId(x.id)}
                className={`flex-1 py-2 rounded-md text-sm font-bold ${x.id === c.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"}`}>{x.name}</button>
            ))}
          </div>
        )}
        {/* Every card is the same three-column table as step 1 — label, Tokens, Worth, figures
            left-aligned in their columns (Alan, 22 Sep: the right-aligned cards looked odd). */}
        <div className="bg-white rounded-lg p-3" data-testid="rate-detail-balance">
          <div className="text-xs font-bold text-slate-500">{ct("settings.rate_change.detail_balance", "Token balance")}</div>
          <table className="w-full text-sm mt-1">
            <thead><tr className="text-xs text-slate-500"><th className="text-left font-bold w-[46%]"><span className="sr-only">Balance</span></th><th className="text-left font-bold py-1">{ct("settings.rate_change.col_tokens", "Tokens")}</th><th className="text-left font-bold py-1">{ct("settings.rate_change.col_worth", "Worth")}</th></tr></thead>
            <tbody>
              <tr className="border-t border-slate-100 align-top">
                <td className="py-1.5 pr-2 font-bold text-slate-900">{c.name}</td>
                <td className="py-1.5 pr-2 font-bold text-slate-900 tabular-nums">{c.balance.before === c.balance.after ? fmtNumber(c.balance.before) : <>{fmtNumber(c.balance.before)}<Arrow />{fmtNumber(c.balance.after)}</>}</td>
                <td className={`py-1.5 font-bold tabular-nums ${worthClass(c.balance.penceBefore, c.balance.penceAfter)}`}>{c.balance.penceBefore === c.balance.penceAfter ? fmtPence(c.balance.penceBefore) : <>{fmtPence(c.balance.penceBefore)}<Arrow />{fmtPence(c.balance.penceAfter)}</>}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="bg-white rounded-lg p-3" data-testid="rate-detail-rewards">
          <div className="text-xs font-bold text-slate-500">{rewardsHeading}</div>
          <table className="w-full text-sm mt-1">
            <thead><tr className="text-xs text-slate-500"><th className="text-left font-bold w-[46%]"><span className="sr-only">Reward</span></th><th className="text-left font-bold py-1">{ct("settings.rate_change.col_tokens", "Tokens")}</th><th className="text-left font-bold py-1">{ct("settings.rate_change.col_worth", "Worth")}</th></tr></thead>
            {groups.map(([g, gLabel]) => {
              // Learn & Earn pass rewards move with their full reward (60%) and are not listed:
              // one line per game is what a parent reads (Alan, 22 Sep).
              const rows = c.earning.before.filter((r) => r.group === g && !/PassReward$/.test(r.key));
              if (!rows.length) return null;
              return (
                <tbody key={g}>
                  <tr><td colSpan={3} className="pt-2 pb-1 text-xs font-bold text-slate-500">{gLabel}</td></tr>
                  {rows.map((r) => {
                    const next = after.get(r.key)?.tokens;
                    return (
                      <tr key={r.key} className="border-t border-slate-100 align-top">
                        <td className="py-1.5 pr-2 font-bold text-slate-900">{labelOf(r)}</td>
                        <td className="py-1.5 pr-2 font-bold text-slate-900 tabular-nums">{tokensCell(r.tokens, next)}</td>
                        <td className={`py-1.5 font-bold tabular-nums ${next === undefined ? "" : worthClass(tokensToPenceAt(r.tokens, fromRate), tokensToPenceAt(next, toRate))}`}>{worthCell(r.tokens, next)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              );
            })}
          </table>
        </div>

        <div className="bg-white rounded-lg p-3" data-testid="rate-detail-money">
          <div className="text-xs font-bold text-slate-500">{ct("settings.rate_change.group_money", "Keep their money value")}</div>
          <table className="w-full text-sm mt-1">
            <thead><tr className="text-xs text-slate-500"><th className="text-left font-bold w-[46%]"><span className="sr-only">Item</span></th><th className="text-left font-bold py-1">{ct("settings.rate_change.col_tokens", "Tokens")}</th><th className="text-left font-bold py-1">{ct("settings.rate_change.col_worth", "Worth")}</th></tr></thead>
            <tbody>
              {c.goals.map((g) => (
                <tr key={g.id} className="border-t border-slate-100 align-top">
                  <td className="py-1.5 pr-2 font-bold text-slate-900">{ct("settings.rate_change.detail_goal_label", "{goal} goal", { goal: g.title })}<span className="block text-xs font-medium text-slate-500">{g.pctBefore === g.pctAfter ? ct("settings.rate_change.pct_there", "{pct}% there", { pct: g.pctAfter }) : <>{g.pctBefore}%<Arrow />{g.pctAfter}%</>}</span></td>
                  <td className="py-1.5 pr-2 font-bold text-slate-900 tabular-nums">{tokensCell(g.before, g.after)}</td>
                  <td className="py-1.5 font-bold tabular-nums text-slate-900">{fmtPence(g.pence)}</td>
                </tr>
              ))}
              {c.spend.before.map((r) => (
                <tr key={r.key} className="border-t border-slate-100 align-top">
                  <td className="py-1.5 pr-2 font-bold text-slate-900">{r.label}</td>
                  <td className="py-1.5 pr-2 font-bold text-slate-900 tabular-nums">{tokensCell(r.tokens, spendAfter.get(r.key)?.tokens)}</td>
                  <td className="py-1.5 font-bold tabular-nums text-slate-900">{fmtGBP(r.tokens, fromRate)}</td>
                </tr>
              ))}
              <tr className="border-t border-slate-100 align-top">
                <td className="py-1.5 pr-2 font-bold text-slate-900">{ct("settings.rate_change.cap_row", "Weekly cap")}</td>
                <td className="py-1.5 pr-2 font-bold text-slate-900 tabular-nums">{c.cap.gbp === null ? "" : tokensCell(c.cap.before, c.cap.after)}</td>
                <td className="py-1.5 font-bold tabular-nums text-slate-900">{c.cap.gbp === null ? ct("settings.rate_change.cap_off", "No weekly cap") : ct("settings.rate_change.cap_week", "{value} a week", { value: fmtPence(Math.round(c.cap.gbp * 100)) })}</td>
              </tr>
              <tr className="border-t border-slate-100 align-top">
                <td className="py-1.5 pr-2 font-bold text-slate-900">{ct("settings.rate_change.vault_row", "Savings Vault")}</td>
                <td className="py-1.5 pr-2"></td>
                <td className="py-1.5 font-bold tabular-nums text-slate-900">{fmtPence(c.vaultPence)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="bg-white border-t border-slate-200 p-4" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}>
        <button type="button" onClick={onClose} className="w-full rounded-lg py-3 text-base font-bold bg-slate-900 text-white" data-testid="rate-detail-done">{ct("settings.rate_change.done", "Done")}</button>
      </div>
    </div>
    </div>,
    document.body
  );
}

/* Boards K1–K3: the child's one-off note on their next visit. `note` is
 * child.pendingRateNote, written by applyRateChange; onClose clears it. */
export function KidRateNote({ note, child, onClose }) {
  if (!note) return null;
  const young = isYoungChild(child);
  const value = tokenValueLabel(note.toRate);
  const goal = note.goal;
  const tile = (n, rate) => (
    <div className="flex-1 bg-amber-50 rounded-xl py-2.5 text-center">
      <div className="text-2xl font-extrabold text-slate-900 tabular-nums">{fmtNumber(n)}</div>
      <div className="text-xs font-bold text-slate-500">{ct("kid.rate_note.at", "tokens at {value}", { value: tokenValueLabel(rate) })}</div>
    </div>
  );
  const isTokens = note.kind === "tokens";
  const more = isTokens ? note.after > note.before : goal.after > goal.before;
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center">
      <div role="dialog" aria-modal="true" data-testid="kid-rate-note" className="bg-white rounded-t-2xl w-full max-w-md p-5 shadow-2xl" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)" }}>
        <div className="text-lg font-extrabold text-slate-900 mb-3">{isTokens
          ? ct("kid.rate_note.tokens_title", "Your tokens have changed")
          : ct("kid.rate_note.goal_title", "Your goal needs {more} tokens", { more: more ? "more" : "fewer" })}</div>
        <div className="flex items-center gap-2">
          {tile(isTokens ? note.before : goal.before, note.fromRate)}
          <Icon id="ic-forward" size={18} style={{ color: "currentColor" }} className="text-slate-500 flex-shrink-0" />
          {tile(isTokens ? note.after : goal.after, note.toRate)}
        </div>
        <div className="text-center text-sm font-bold text-slate-700 mt-2.5">{isTokens
          ? ct("kid.rate_note.money_line", "{before} before, {after} now", { before: fmtPence(note.penceBefore), after: fmtPence(note.penceAfter) })
          : ct("kid.rate_note.goal_money_line", "{goal}, {price} before, {price} now", { goal: goal.title, price: fmtPence(goal.pence) })}</div>
        {!isTokens && (
          <div className="mt-3">
            <div className="flex justify-between text-xs font-bold text-slate-500 mb-1"><span>{goal.title}</span><span>{ct("kid.rate_note.of_tokens", "{have} of {need} tokens", { have: fmtNumber(Math.min(note.have, goal.after)), need: fmtNumber(goal.after) })}</span></div>
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-blue-600 rounded-full" style={{ width: `${Math.min(100, Math.round((note.have / Math.max(1, goal.after)) * 100))}%` }} /></div>
          </div>
        )}
        <p className="text-sm text-slate-700 mt-3">{isTokens
          ? (young
            ? (more ? ct("kid.rate_note.body_young_more", "You have more tokens now, but each one is worth less. It's the same money as before.") : ct("kid.rate_note.body_young_fewer", "You have fewer tokens now, but each one is worth more. It's the same money as before."))
            : ct("kid.rate_note.body", "Tokens are now worth {value}, so your {from} tokens became {to}. It's the same money.", { value, from: fmtNumber(note.before), to: fmtNumber(note.after) }))
          : (young
            ? ct("kid.rate_note.goal_body_young", "Your {goal} needs {more} tokens now, but it still costs the same money.", { goal: goal.title, more: more ? "more" : "fewer" })
            : ct("kid.rate_note.goal_body", "Tokens are now worth {value}, so your {goal} needs {to} tokens instead of {from}. It still costs the same money.", { value, goal: goal.title, to: fmtNumber(goal.after), from: fmtNumber(goal.before) }))}</p>
        {isTokens && goal && (
          <p className="text-sm text-slate-700 mt-2">{young
            ? ct("kid.rate_note.goal_line_young", "Your {goal} is just as close as it was.", { goal: goal.title })
            : ct("kid.rate_note.goal_line", "Your {goal} goal is now {n} tokens. You're just as close as you were.", { goal: goal.title, n: fmtNumber(goal.after) })}</p>
        )}
        <button type="button" onClick={onClose} className="mt-4 w-full bg-navy text-white font-bold rounded-lg py-3" data-testid="kid-rate-note-close">{ct("kid.rate_note.got_it", "Got it")}</button>
      </div>
    </div>,
    document.body
  );
}
